import Button from "@/Components/Button/Button";
import Textfield from "@/Components/Input/Textfield";
import { useModalMeta, useSetModal } from "@/shared/atoms/useModal";
import { useAddCartItem, useReplaceCartItem } from "@/shared/hooks/useCartHook";
import useFocusAndCursorToEnd from "@/shared/hooks/useFocusAndCursorToEndHook";
import useIsMobileHook from "@/shared/hooks/useIsMobileHook";
import { getRandomNegativeId } from "@/shared/utils/helpers";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import ButtonGroup from "../ButtonGroup/ButtonGroup";
import Counter from "../Counter/Counter";
import TextfieldWithNumberBoard from "../Input/TextfieldWithNumberBoard";
import Modal, { ModalProps } from "../Modal/Modal";
import ModalAction from "../Modal/ModalAction";
import ModalTitle from "../Modal/ModalTitle";
import useVatHook from "@/shared/hooks/useVatHook";

const PosOrderProductModal = ({ onExit, ...props }: ModalProps) => {
  const ref = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();
  const isMobile = useIsMobileHook();
  const { vats } = useVatHook();
  const [
    {
      product = {
        price: undefined,
        description: "",
        vat_percentage: vats[1].percentage,
        name: t("posOrders.openProduct"),
        item_id: getRandomNegativeId(),
        quantity: 1,
      },
      index,
    },
  ] = useModalMeta();
  const [quantity, setQuantity] = useState(product?.quantity || 1);
  const { register, errors, handleSubmit, setValue } = useForm();
  const replaceCartItem = useReplaceCartItem();
  const addCartItem = useAddCartItem();
  useFocusAndCursorToEnd(ref);
  const [vatPercentage, setVatPercentage] = useState(
    vats.find(
      (v) =>
        v.id === product?.vat_percentage ||
        v.percentage === product?.vat_percentage
    )?.percentage
  );
  const setModal = useSetModal();
  const isEdit = index !== undefined;

  const onSubmit = (values) => {
    const newProduct = {
      ...product,
      ...values,
      vat_percentage: vatPercentage,
      name: values.name || t("posOrders.openProduct"),
      quantity: quantity,
      item_id: getRandomNegativeId(),
    };

    isEdit ? replaceCartItem(newProduct, index) : addCartItem(newProduct);
    setModal("", {});
  };

  return (
    <Modal width="50rem" onExit={onExit} {...props}>
      <ModalTitle mb="2rem" p="0">
        {t("posOrders.customProduct")}
      </ModalTitle>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-8 overflow-hidden"
      >
        <Textfield
          label={t("posOrders.name")}
          placeholder={t("posOrders.name")}
          name="name"
          defaultValue={product?.name}
          error={errors?.name?.message}
          ref={register({})}
        />

        <Textfield
          label={t("posOrders.description")}
          as="textarea"
          width="100%"
          rows="2"
          placeholder={t("posOrders.description")}
          name="description"
          error={errors?.description?.message}
          defaultValue={product?.description}
          ref={register({
            required: {
              value: false,
              message: t("fieldRequired"),
            },
          })}
        />

        <TextfieldWithNumberBoard
          label={t("posOrders.price")}
          type="number"
          step=".01"
          placeholder={t("posOrders.price")}
          name="price"
          setValue={(value) => setValue("price", value)}
          align="right"
          defaultValue={parseFloat(product?.price) > 0 ? product.price : ""}
          ref={(e) => {
            register(e, {
              required: {
                value: true,
                message: t("fieldRequired"),
              },
            });
            ref.current = e;
          }}
          error={errors?.price?.message}
          showNumPad={true}
        />

        <ButtonGroup
          options={vats.map((v) => ({
            id: v.percentage,
            label: t(v.label),
            onClick: () => setVatPercentage(v.percentage),
          }))}
          selected={vatPercentage}
          buttonType="button"
          size="medium"
          label={t("posOrders.vat")}
        />
        <Counter
          label="Quantity"
          value={quantity}
          onDecreaseClick={() => quantity > 1 && setQuantity(quantity - 1)}
          onIncreaseClick={() => setQuantity(quantity + 1)}
          variant={isMobile ? "medium" : "large"}
        />

        <ModalAction>
          <Button>{t("posOrders.save")}</Button>
        </ModalAction>
      </form>
    </Modal>
  );
};

export default PosOrderProductModal;
